import { z } from 'zod';
// ---------------------------------------------------------------------------
// `X-Client` — how a player client describes itself (ios-app-arch §9.2,
// ADR-042 rule 2). One grammar, one builder and one parser for the api, the
// web and the app:
//
//   X-Client: app=ios; v=1.0.0; build=42; device=ipad; os=17.4; code=a1b2c3d
//   X-Client: app=web; build=manual-a1b2c3d; device=ipad
//
// `;`-separated `key=value`, at most 256 bytes of printable ASCII. Unknown keys
// are ignored. A header that breaks the grammar counts as ABSENT: the api never
// answers 400 or 426 for it and records nothing. The api acts on what the parser
// returns — the 426 gate below `PLAYER_MIN_IOS_BUILD` and
// `internal.player_sessions.client`.
//
// The grammar only ever WIDENS. An App Store build keeps sending the header its
// own copy of this module built, for months; a key made required, or a value
// the parser stops reading, turns that build into one the server can never ask
// to update.
// ---------------------------------------------------------------------------
/** The header's name. Node lower-cases header names; Express's `req.get` matches either case. */
export const CLIENT_HEADER_NAME = 'X-Client';
/** A longer header counts as absent (arch §9.2, §18). Every header the builder writes is shorter. */
export const CLIENT_HEADER_MAX_BYTES = 256;
/**
 * `426 CLIENT_UPGRADE_REQUIRED` — an app build below `PLAYER_MIN_IOS_BUILD`
 * (arch §9.3, ADR-042 rule 5). The app answers it from any call by stopping and
 * showing «Обновите приложение». Frozen with the codes the app acts on (ADR-042
 * rule 4); RFC 9110 wants an `Upgrade` header with 426, but there is no
 * protocol to name, so clients key on the status and this code.
 */
export const CLIENT_VERSION_ERROR_CODES = {
    CLIENT_UPGRADE_REQUIRED: 'CLIENT_UPGRADE_REQUIRED',
};
export const clientAppSchema = z.enum(['ios', 'web']);
/**
 * The device the client runs on. The web tells an iPad from a Mac by the page
 * (`maxTouchPoints`), since iPad Safari's User-Agent says «Macintosh».
 */
export const clientDeviceSchema = z.enum(['ipad', 'iphone', 'mac', 'windows', 'android', 'other']);
/** Free-form values — the web's image build id (`manual-<sha>`, `v1.4.0`, `dev`), `os`, `code`. */
const tokenSchema = z.string().regex(/^[A-Za-z0-9._-]{1,64}$/);
/** The App Store build number: monotonic per bundle id under EAS remote versioning (ADR-042). */
const IOS_BUILD_DIGITS = /^\d{1,9}$/;
const iosClientSchema = z.object({
    app: z.literal('ios'),
    /** CFBundleShortVersionString, MAJOR.MINOR.PATCH — for people reading the census. */
    v: z.string().regex(/^\d{1,4}\.\d{1,4}\.\d{1,4}$/),
    /** What the 426 gate compares with `PLAYER_MIN_IOS_BUILD`. */
    build: z.number().int().min(0).max(999_999_999),
    device: clientDeviceSchema,
    os: tokenSchema.optional(),
    /** The JavaScript commit the app runs (an over-the-air update changes it, not `build`). */
    code: tokenSchema.optional(),
});
const webClientSchema = z.object({
    app: z.literal('web'),
    build: tokenSchema,
    device: clientDeviceSchema,
    os: tokenSchema.optional(),
    code: tokenSchema.optional(),
});
const clientHeaderSchema = z.discriminatedUnion('app', [iosClientSchema, webClientSchema]);
/** The order the builder writes, so one client is always one string. */
const KEY_ORDER = ['app', 'v', 'build', 'device', 'os', 'code'];
/** Printable ASCII only, so the length in characters is the length in bytes. */
const PRINTABLE_ASCII = /^[\x20-\x7E]*$/;
/**
 * Reads an `X-Client` value. `null` when it is absent or malformed — anything
 * outside the grammar, a key given twice included.
 */
export function parseClientHeader(raw) {
    if (typeof raw !== 'string' ||
        raw.length > CLIENT_HEADER_MAX_BYTES ||
        !PRINTABLE_ASCII.test(raw)) {
        return null;
    }
    const fields = new Map();
    for (const segment of raw.split(';')) {
        if (segment.trim() === '')
            continue;
        const eq = segment.indexOf('=');
        const key = eq < 0 ? '' : segment.slice(0, eq).trim();
        const value = eq < 0 ? '' : segment.slice(eq + 1).trim();
        if (key === '' || value === '' || fields.has(key))
            return null;
        fields.set(key, value);
    }
    const candidate = Object.fromEntries(fields);
    // Only plain digits become a number: `Number()` alone would read `0x2A` as 42.
    if (candidate.app === 'ios' &&
        typeof candidate.build === 'string' &&
        IOS_BUILD_DIGITS.test(candidate.build)) {
        candidate.build = Number(candidate.build);
    }
    const parsed = clientHeaderSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
}
/**
 * Writes the `X-Client` value for `client`: its keys in one fixed order, absent
 * ones left out. Throws on a value outside the grammar rather than send a
 * header the api would count as absent — for the app that would be a build the
 * server can never ask to update.
 */
export function buildClientHeader(client) {
    const parsed = clientHeaderSchema.safeParse(client);
    if (!parsed.success) {
        const issues = parsed.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`);
        throw new Error(`X-Client outside the grammar — ${issues.join('; ')}`);
    }
    const values = parsed.data;
    return KEY_ORDER.filter((key) => values[key] !== undefined)
        .map((key) => `${key}=${String(values[key])}`)
        .join('; ');
}
//# sourceMappingURL=client-header.js.map